import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CreditCard, DollarSign, TrendingUp, CheckCircle2, XCircle, Clock,
  RefreshCw, Search, Filter, Download, Receipt, Wallet, Smartphone,
  Building2, AlertCircle, Calculator, Copy, Check, Ban
} from 'lucide-react';
import { api } from '@/lib/api';
import { ExportButton } from '@/components/ExportButton';

interface PaymentMethod {
  id: number;
  name: string;
  code: string;
  provider: string;
  feePercentage: number;
  feeFixed: number;
  minAmount: number;
  maxAmount: number | null;
}

interface Transaction {
  id: number;
  transactionNumber: string;
  shipmentTracking?: string;
  customerName?: string;
  paymentMethod: string;
  amount: number;
  currency: string;
  status: string;
  fees: number;
  netAmount: number;
  gatewayReference?: string;
  paidAt?: string;
  expiresAt?: string;
  failureReason?: string;
  createdAt: string;
}

interface PaymentStats {
  total: number;
  completed: number;
  pending: number;
  failed: number;
  totalAmount: number;
  totalFees: number;
  netRevenue: number;
  byMethod: { method: string; count: number; total: number }[];
  recentTransactions: Transaction[];
}

export default function PaymentsPage() {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<PaymentStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFeesModal, setShowFeesModal] = useState(false);
  const [feesData, setFeesData] = useState<any>(null);
  const [createForm, setCreateForm] = useState({
    shipmentId: '',
    customerId: '',
    paymentMethodCode: 'cod',
    amount: 0
  });
  const [feesForm, setFeesForm] = useState({
    paymentMethodCode: 'fawry',
    amount: 500
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [methodsRes, statsRes] = await Promise.all([
        api.get('/api/payments/methods'),
        api.get('/api/payments/stats')
      ]);

      if (methodsRes.data.success) setPaymentMethods(methodsRes.data.data);
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
        setTransactions(statsRes.data.data.recentTransactions || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateFees = async () => {
    try {
      const res = await api.post('/api/payments/calculate-fees', feesForm);
      if (res.data.success) {
        setFeesData(res.data.data);
        setShowFeesModal(true);
      }
    } catch (error) {
      console.error('Error calculating fees:', error);
    }
  };

  const createTransaction = async () => {
    if (!createForm.amount || createForm.amount <= 0) {
      alert('يرجى إدخال مبلغ صحيح');
      return;
    }
    try {
      const res = await api.post('/api/payments/create', {
        shipmentId: createForm.shipmentId ? Number(createForm.shipmentId) : null,
        customerId: createForm.customerId ? Number(createForm.customerId) : null,
        paymentMethodCode: createForm.paymentMethodCode,
        amount: createForm.amount
      });
      if (res.data.success) {
        alert('تم إنشاء المعاملة بنجاح');
        setShowCreateModal(false);
        loadData();
      } else {
        alert(res.data.message);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'حدث خطأ');
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed': return <Badge className="bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3 ml-1" />مكتمل</Badge>;
      case 'pending': return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="w-3 h-3 ml-1" />قيد الانتظار</Badge>;
      case 'failed': return <Badge className="bg-red-100 text-red-700"><XCircle className="w-3 h-3 ml-1" />فشل</Badge>;
      case 'cancelled': return <Badge className="bg-gray-100 text-gray-700"><Ban className="w-3 h-3 ml-1" />ملغى</Badge>;
      case 'awaiting_payment': return <Badge className="bg-blue-100 text-blue-700"><Clock className="w-3 h-3 ml-1" />في انتظار الدفع</Badge>;
      case 'expired': return <Badge className="bg-orange-100 text-orange-700"><XCircle className="w-3 h-3 ml-1" />منتهي</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getMethodIcon = (code: string) => {
    switch (code) {
      case 'cod': return <Wallet className="w-4 h-4" />;
      case 'fawry': return <Building2 className="w-4 h-4" />;
      case 'paymob_card': return <CreditCard className="w-4 h-4" />;
      case 'vodafone_cash': return <Smartphone className="w-4 h-4" />;
      case 'instapay': return <Smartphone className="w-4 h-4" />;
      default: return <CreditCard className="w-4 h-4" />;
    }
  };

  const getMethodName = (code: string) => {
    const method = paymentMethods.find(m => m.code === code);
    return method?.name || code;
  };

  const formatCurrency = (value: number) => `${value.toLocaleString('ar-EG', { maximumFractionDigits: 2 })} ج.م`;

  const filteredTransactions = transactions.filter(t => {
    if (statusFilter && t.status !== statusFilter) return false;
    if (search && !t.transactionNumber.toLowerCase().includes(search.toLowerCase()) &&
        !t.customerName?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const exportColumns = [
    { key: 'transactionNumber', label: 'رقم المعاملة' },
    { key: 'customerName', label: 'العميل' },
    { key: 'paymentMethod', label: 'طريقة الدفع' },
    { key: 'amount', label: 'المبلغ' },
    { key: 'status', label: 'الحالة' },
    { key: 'createdAt', label: 'التاريخ', format: (v: string) => new Date(v).toLocaleDateString('ar-EG') }
  ];

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            بوابة الدفع الإلكتروني
          </h1>
          <p className="text-muted-foreground mt-1">إدارة المعاملات المالية وطرق الدفع</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowFeesModal(true)} variant="outline" className="gap-2">
            <Calculator className="w-4 h-4" />
            حاسب الرسوم
          </Button>
          <Button onClick={() => setShowCreateModal(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
            <CreditCard className="w-4 h-4" />
            معاملة جديدة
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-emerald-50 to-teal-100 border-emerald-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-emerald-700">إجمالي الإيرادات</p>
                  <p className="text-xl font-bold text-emerald-900">{formatCurrency(stats.totalAmount)}</p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-700">صافي الإيراد</p>
                  <p className="text-xl font-bold text-blue-900">{formatCurrency(stats.netRevenue)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-green-700">المعاملات المكتملة</p>
                  <p className="text-xl font-bold text-green-900">{stats.completed}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-amber-700">الرسوم المحصولة</p>
                  <p className="text-xl font-bold text-amber-900">{formatCurrency(stats.totalFees)}</p>
                </div>
                <Receipt className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Payment Methods */}
      <Card>
        <CardHeader>
          <CardTitle>طرق الدفع المتاحة</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {paymentMethods.map((method) => (
              <div key={method.id} className="p-4 border rounded-lg hover:border-emerald-500 transition">
                <div className="flex items-center gap-2 mb-2">
                  {getMethodIcon(method.code)}
                  <p className="font-semibold text-sm">{method.name}</p>
                </div>
                <p className="text-xs text-gray-500">
                  {method.feePercentage > 0 ? `رسم: ${method.feePercentage}% + ${method.feeFixed} ج.م` : 'بدون رسوم'}
                </p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Payment Methods by Volume */}
      {stats && stats.byMethod.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>توزيع طرق الدفع</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.byMethod.map((item, i) => {
                const total = stats.byMethod.reduce((sum, m) => sum + m.total, 0);
                const percentage = total > 0 ? (item.total / total) * 100 : 0;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-40 flex items-center gap-2">
                      {getMethodIcon(item.method)}
                      <span className="text-sm font-medium">{getMethodName(item.method)}</span>
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-8 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-end px-3 text-white text-sm font-semibold transition-all"
                        style={{ width: `${percentage}%` }}
                      >
                        {formatCurrency(item.total)}
                      </div>
                    </div>
                    <div className="w-20 text-sm text-gray-600 text-left">{item.count} معاملة</div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle>آخر المعاملات</CardTitle>
            <div className="flex gap-2">
              <ExportButton data={transactions} filename="transactions" columns={exportColumns} title="المعاملات" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="p-4 border-b">
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 min-w-[200px] relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="بحث..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pr-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg bg-white"
              >
                <option value="">كل الحالات</option>
                <option value="completed">مكتمل</option>
                <option value="pending">قيد الانتظار</option>
                <option value="failed">فشل</option>
                <option value="cancelled">ملغى</option>
              </select>
              <Button onClick={loadData} variant="outline">
                <Filter className="w-4 h-4 ml-2" />تطبيق
              </Button>
            </div>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            </div>
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">لا توجد معاملات</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-right p-3 font-semibold">رقم المعاملة</th>
                    <th className="text-right p-3 font-semibold">العميل</th>
                    <th className="text-right p-3 font-semibold">طريقة الدفع</th>
                    <th className="text-right p-3 font-semibold">المبلغ</th>
                    <th className="text-right p-3 font-semibold">الحالة</th>
                    <th className="text-right p-3 font-semibold">التاريخ</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map((t) => (
                    <tr key={t.id} className="border-b hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm">{t.transactionNumber}</td>
                      <td className="p-3">{t.customerName || '-'}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          {getMethodIcon(t.paymentMethod)}
                          <span className="text-sm">{getMethodName(t.paymentMethod)}</span>
                        </div>
                      </td>
                      <td className="p-3 font-semibold">{formatCurrency(t.amount)}</td>
                      <td className="p-3">{getStatusBadge(t.status)}</td>
                      <td className="p-3 text-sm text-gray-500">
                        {new Date(t.createdAt).toLocaleString('ar-EG')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fees Calculator Modal */}
      {showFeesModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5 text-emerald-500" />
                حاسب الرسوم
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">طريقة الدفع</label>
                <select
                  value={feesForm.paymentMethodCode}
                  onChange={(e) => setFeesForm({ ...feesForm, paymentMethodCode: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {paymentMethods.map(m => (
                    <option key={m.id} value={m.code}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">المبلغ (ج.م)</label>
                <Input
                  type="number"
                  value={feesForm.amount}
                  onChange={(e) => setFeesForm({ ...feesForm, amount: Number(e.target.value) })}
                />
              </div>
              {feesData && (
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span>المبلغ:</span>
                    <span className="font-semibold">{formatCurrency(feesData.amount)}</span>
                  </div>
                  <div className="flex justify-between text-red-600">
                    <span>الرسوم:</span>
                    <span className="font-semibold">- {formatCurrency(feesData.fees)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between text-lg">
                    <span className="font-bold">الصافي:</span>
                    <span className="font-bold text-emerald-600">{formatCurrency(feesData.netAmount)}</span>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={calculateFees} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  احتساب
                </Button>
                <Button variant="outline" onClick={() => { setShowFeesModal(false); setFeesData(null); }} className="flex-1">
                  إغلاق
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Create Transaction Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>إنشاء معاملة جديدة</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">طريقة الدفع</label>
                <select
                  value={createForm.paymentMethodCode}
                  onChange={(e) => setCreateForm({ ...createForm, paymentMethodCode: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  {paymentMethods.map(m => (
                    <option key={m.id} value={m.code}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">المبلغ (ج.م)</label>
                <Input
                  type="number"
                  value={createForm.amount}
                  onChange={(e) => setCreateForm({ ...createForm, amount: Number(e.target.value) })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">رقم الشحنة (اختياري)</label>
                <Input
                  type="number"
                  value={createForm.shipmentId}
                  onChange={(e) => setCreateForm({ ...createForm, shipmentId: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">رقم العميل (اختياري)</label>
                <Input
                  type="number"
                  value={createForm.customerId}
                  onChange={(e) => setCreateForm({ ...createForm, customerId: e.target.value })}
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button onClick={createTransaction} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  إنشاء
                </Button>
                <Button variant="outline" onClick={() => setShowCreateModal(false)} className="flex-1">
                  إلغاء
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
